import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import React, { useCallback, useEffect, useRef } from 'react'
import {
  clearTransientAuthStorage,
  getB2CAuthority,
  setPostLoginDestination,
} from 'helpers/authFlow'
import { isMobile } from 'react-device-detect'
import { ssoAPI } from '../../api/ssoAPI'
import { setLogoutRedirectUri } from '../../helpers/logoutRedirect'

const DirectSSO: React.FC = () => {
  const { instance, inProgress } = useMsal()
  const startedRef = useRef(false)

  const fetchDomainHint = useCallback(async (clientReference: string) => {
    try {
      const response = await ssoAPI.getClientDomain(clientReference)
      if (response?.data?.domain) {
        return { domain_hint: response.data.domain }
      }
    } catch (err) {
      console.error('Error fetching client domain:', err)
    }
    return undefined
  }, [])

  useEffect(() => {
    if (startedRef.current) {
      console.log('[DirectSSO] Already started, skipping re-entry')
      return
    }

    if (inProgress !== InteractionStatus.None) {
      console.log(
        '[DirectSSO] MSAL interaction in progress, waiting:',
        inProgress,
      )
      return
    }

    console.log('[DirectSSO] Starting DirectSSO flow')

    // Diagnostic: Log MSAL state before proceeding
    const msalAccounts = instance.getAllAccounts()
    const msalActiveAccount = instance.getActiveAccount()
    console.log('[DirectSSO] Pre-flight MSAL state:', {
      accountCount: msalAccounts.length,
      hasActiveAccount: !!msalActiveAccount,
      activeAccountUsername: msalActiveAccount?.username,
      localStorage: {
        loginPolicy: localStorage.getItem('loginPolicy'),
        msalKeys: Object.keys(localStorage).filter((k) => k.includes('msal'))
          .length,
      },
    })

    startedRef.current = true
    ;(async () => {
      // Always grab the original query so we can forward it unmodified
      const currentUrl = new URL(window.location.href)
      const originalQuery = currentUrl.search // includes leading '?', or '' if none

      const params = new URLSearchParams(currentUrl.search)
      const logoutRedirect = params.get('logout_redirect')
      const returnTo = params.get('return_to')

      setLogoutRedirectUri(logoutRedirect)
      if (returnTo) {
        setPostLoginDestination(returnTo)
      }

      // 1) If mobile, redirect *before* kicking off any MSAL here
      try {
        if (isMobile) {
          // Ask backend which mobile base URL to use
          const response = await ssoAPI.getMobileClient(window.location.origin)
          const mobileBase = response?.data?.mobileUrl as string | undefined

          if (mobileBase) {
            const mobileBaseUrl = new URL(mobileBase)
            const alreadyOnMobile =
              window.location.origin.toLowerCase() ===
              mobileBaseUrl.origin.toLowerCase()

            if (!alreadyOnMobile) {
              // Build target: <mobileBase>/sso + original query intact
              const target = new URL('/sso.html', mobileBaseUrl)
              // Preserve exact incoming query parameters (id_token_hint, domain_hint, etc.)
              const targetHref = `${target.toString()}${originalQuery || ''}`

              // Use replace to avoid leaving a "dead" page in history
              window.location.replace(targetHref)
              return // stop executing here; the mobile app will handle MSAL
            }
            // If we're *already* on the mobile origin, just proceed to MSAL below.
          }
        }
      } catch (e) {
        // If the mobile URL lookup fails, fall through to desktop flow
        console.warn(
          'Mobile handoff skipped (lookup failed). Proceeding with desktop MSAL.',
          e,
        )
      }

      // 2) Desktop (or already on mobile origin): proceed with your existing MSAL loginRedirect

      const loginHint = params.get('login_hint') || undefined
      const domainHintParam = params.get('domain_hint') || undefined
      const appId = params.get('appId') || params.get('appid') || undefined
      const idTokenHint = params.get('id_token_hint') || undefined

      // Validate id_token_hint hasn't expired
      if (idTokenHint) {
        try {
          const parts = idTokenHint.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]))
            const exp = payload.exp
            const now = Math.floor(Date.now() / 1000)

            if (exp && exp < now) {
              console.error('[DirectSSO] id_token_hint has expired', {
                exp,
                now,
                expired: now - exp,
              })
              // Token expired - could redirect to error page or partner IDP
              // For now, proceed anyway and let B2C handle the error
            } else {
              console.log('[DirectSSO] id_token_hint is valid', {
                expiresIn: exp ? exp - now : 'unknown',
              })
            }
          }
        } catch (e) {
          console.warn('[DirectSSO] Could not decode id_token_hint', e)
        }
      }

      const loginRequest: {
        scopes: string[]
        loginHint?: string
        authority?: string
        extraQueryParameters?: Record<string, string>
        redirectStartPage?: string
      } = {
        scopes: [
          `https://jyrneyb2c${process.env.REACT_APP_B2C_ENVIRONMENTDETAILS}.onmicrosoft.com/users-api/Users.All`,
        ],
      }

      const extra: Record<string, string> = {}

      if (appId) {
        const fetched = await fetchDomainHint(appId)
        if (fetched) Object.assign(extra, fetched)
        extra.appid = appId
        extra.logonLocation = 'jyrney'
        localStorage.setItem('logonLocation', 'jyrney')
      } else if (domainHintParam) {
        extra.domain_hint = domainHintParam
      }

      if (idTokenHint) {
        extra.customIdHint = idTokenHint
      }

      if (Object.keys(extra).length > 0) {
        loginRequest.extraQueryParameters = extra
      }

      if (loginHint) {
        loginRequest.loginHint = loginHint
      }

      loginRequest.authority = getB2CAuthority('B2C_1A_SIGNUP_SIGNIN')

      try {
        if (!isMobile) {
          // Desktop: return users to this origin after B2C completes
          loginRequest.redirectStartPage = window.location.origin
        } else {
          // If we're here on mobile origin (mobile handoff already happened), prefer the root
          loginRequest.redirectStartPage = window.location.origin
        }
      } catch {
        loginRequest.redirectStartPage = window.location.origin
      }

      try {
        clearTransientAuthStorage()
      } catch (e) {
        console.warn('Transient auth storage cleanup failed', e)
      }
      setLogoutRedirectUri(logoutRedirect)
      if (returnTo) {
        setPostLoginDestination(returnTo)
      }
      try {
        localStorage.setItem('loginPolicy', 'B2C_1A_SIGNUP_SIGNIN')
      } catch (e) {
        console.warn('[DirectSSO] Failed to persist loginPolicy', e)
      }

      console.log('[DirectSSO] Initiating loginRedirect with request:', {
        scopes: loginRequest.scopes,
        authority: loginRequest.authority,
        loginHint: loginRequest.loginHint,
        extraQueryParameters: loginRequest.extraQueryParameters,
        redirectStartPage: loginRequest.redirectStartPage,
      })

      instance.loginRedirect(loginRequest).catch((err) => {
        console.error('[DirectSSO] MSAL loginRedirect failed:', err)
        // Allow retry after a failure
        startedRef.current = false
      })
    })()
  }, [instance, inProgress, fetchDomainHint])

  return (
    <div>
      <p>Redirecting for authentication...</p>
    </div>
  )
}

export default DirectSSO
